const IDENTIFIER_REGEX = /^(?:(\d+)-)?([A-Z]{3})(\d{5}|995)([A-Z])$/;

const KVPS_BRAND_LOGO: Record<string, string> = {
  A: "/brands/audi.png",
  C: "/brands/skoda.png",
  N: "/brands/volkswagen_utility.png",
  S: "/brands/seat.png",
  V: "/brands/volkswagen.png",
};

/** Single-letter brand code embedded in KVPS identifiers (matches Prisma `BrandLetter`). */
export type KvpsBrandLetter = "A" | "V" | "N" | "S" | "C";

const KVPS_BRAND_DISPLAY_LABELS: Record<KvpsBrandLetter, string> = {
  A: "Audi",
  V: "Volkswagen",
  N: "Volkswagen Utilitaires",
  S: "Seat",
  C: "Skoda",
};

/** Maps a KVPS / MuleSoft single-letter brand code to its display label. */
export function kvpsBrandLetterToDisplayLabel(
  value: string | null | undefined,
): string | null {
  if (!value?.trim()) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized in KVPS_BRAND_DISPLAY_LABELS) {
    return KVPS_BRAND_DISPLAY_LABELS[normalized as KvpsBrandLetter];
  }
  return value.trim();
}

/**
 * Maps LEMA export column "Marque métier" (VW, VU, AUDI, …) to the KVPS brand letter.
 * Used by LEMA seed/import; keep in sync with business rules from the referential team.
 */
export function mapLemaBusinessBrandToKvpsBrandLetter(
  raw: string | null | undefined,
): KvpsBrandLetter | null {
  const normalized = raw?.trim().toUpperCase().replace(/\s+/g, " ") ?? "";
  if (!normalized) return null;

  switch (normalized) {
    case "VW":
      return "V";
    case "VU":
      return "N";
    case "AUDI":
      return "A";
    case "SKODA":
      return "C";
    case "SEAT":
    case "CUPRA":
      return "S";
    default:
      return null;
  }
}

export type Kvps = {
  identifier: string;
  label: string;
  parts: {
    prefix: string; // TODO: might be renamed later...
    country: string;
    wholesalerNumber: string;
    brand: string;
  };
  kind: KvpsKind;
};

export enum KvpsKind {
  AVS,
  INTERNAL,
  RETAIL,
  UNKNOWN,
}

export function buildKvps(
  identifier: Kvps["identifier"],
  label: Kvps["label"],
) {
  const match = identifier.match(IDENTIFIER_REGEX);

  if (!match) {
    throw new Error(`Invalid KVP identifier: ${identifier}`);
  }

  const kvps: Kvps = {
    identifier,
    label,
    parts: {
      prefix: match[1],
      country: match[2],
      wholesalerNumber: match[3],
      brand: match[4],
    },
    kind: KvpsKind.UNKNOWN,
  };

  kvps.kind = getKvpsKind(kvps);

  return kvps;
}

export function findKvps(
  identifier: Kvps["identifier"],
  candidates: Kvps[],
): Kvps | undefined {
  return candidates.find((kvp) => kvp.identifier === identifier);
}

export function formatKvps(kvps: Kvps) {
  return `${kvps.identifier} – ${kvps.label}`;
}

/** Placeholder label used until GSB returns a real organization name. */
const KVPS_PLACEHOLDER_LABEL = "lorem ipsum";

export function resolveKvpsDisplayLabel(
  label: string | null | undefined,
): string | null {
  const trimmed = label?.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === KVPS_PLACEHOLDER_LABEL) return null;
  return trimmed;
}

export function getKvpsBrandLogoPath(
  kvps: Kvps | null | undefined,
): string | null {
  if (!kvps?.parts?.brand) return null;
  return KVPS_BRAND_LOGO[kvps.parts.brand] ?? null;
}

export function getBrandLogoPathFromKvpsIdentifier(
  identifier: string | null | undefined,
): string | null {
  const filter = parseKvpsWholesalerAndBrand(identifier);
  if (!filter) return null;
  return KVPS_BRAND_LOGO[filter.brandLetter] ?? null;
}

export function getKvpsKind(kvps: Kvps): KvpsKind {
  if (kvps.parts.country !== "FRA") {
    return KvpsKind.UNKNOWN;
  }

  if (kvps.parts.wholesalerNumber === "995") {
    return KvpsKind.INTERNAL;
  }

  // TODO:
  if (false) {
    return KvpsKind.AVS;
  }

  return KvpsKind.RETAIL;
}

/** Wholesaler + brand extracted from a KVPS identifier (LEMA, brand logo, draft access, …). */
export type KvpsWholesalerBrand = {
  wholesalerNumber: string;
  brandLetter: KvpsBrandLetter;
};

const KVPS_BRAND_LETTERS: KvpsBrandLetter[] = ["A", "V", "N", "S", "C"];

/** Parses wholesaler number and brand letter from a KVPS identifier. */
export function parseKvpsWholesalerAndBrand(
  identifier: string | null | undefined,
): KvpsWholesalerBrand | null {
  if (!identifier?.trim()) return null;

  const match = identifier.trim().match(IDENTIFIER_REGEX);
  if (!match) return null;

  const brandLetter = match[4] as KvpsBrandLetter;
  if (!KVPS_BRAND_LETTERS.includes(brandLetter)) return null;

  return {
    wholesalerNumber: match[3],
    brandLetter,
  };
}

const DOM_KVPS_WHOLESALER_NUMBERS = new Set(["01563", "01565", "01509"]);

export function resolveKvpsWholesalerNumber(
  draftKvpsIdentifier: string | null | undefined,
  sessionKvps: Kvps | null | undefined,
): string | null {
  return (
    parseKvpsWholesalerAndBrand(draftKvpsIdentifier)?.wholesalerNumber ??
    sessionKvps?.parts?.wholesalerNumber ??
    null
  );
}

export function isKvpsEligibleForRentChoice(
  draftKvpsIdentifier: string | null | undefined,
  sessionKvps?: Kvps | null,
): boolean {
  const wholesalerNumber = resolveKvpsWholesalerNumber(
    draftKvpsIdentifier,
    sessionKvps,
  );
  if (!wholesalerNumber) return false;
  return !DOM_KVPS_WHOLESALER_NUMBERS.has(wholesalerNumber);
}

export function isDomVehicleKvps(
  kvpsIdentifier: string | null | undefined,
  sessionKvps?: Kvps | null,
): boolean {
  const wholesalerNumber = resolveKvpsWholesalerNumber(
    kvpsIdentifier,
    sessionKvps,
  );
  if (!wholesalerNumber) return false;
  return DOM_KVPS_WHOLESALER_NUMBERS.has(wholesalerNumber);
}
